import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import BeatLoader from "react-spinners/BeatLoader";
import axios from "axios";

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Website Services");

  useEffect(() => {
    AOS.init({ duration: 1000 });
    
    const fetchPlans = async () => {
      try {
        const res = await axios.get(window.API_URL + "/plans");
        if (Array.isArray(res.data)) {
          setPlans(res.data);
        } else {
          console.error("Expected array for plans, but got:", res.data);
          setPlans([]);
        }
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching plans:", err);
        setIsLoading(false);
      }
    };
    
    fetchPlans();
  }, []);

  const filteredPlans = Array.isArray(plans) ? plans.filter(plan => plan.category === activeTab) : [];

  return (
    <div className="pricing-container urbanist pt-24 pb-20 min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-[#fff0e6] to-transparent z-0"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight" data-aos="fade-down">
            Simple, Transparent <span className="text-[#f06600]">Pricing</span>
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="100">
            Choose the perfect plan for your business needs. No hidden fees, just pure value.
          </p>
        </div>

        {/* Custom Tab Selector */}
        <div className="flex justify-center mb-16" data-aos="zoom-in" data-aos-delay="200">
          <div className="bg-white p-1 rounded-full shadow-md inline-flex border border-gray-100">
            <button
              onClick={() => setActiveTab("Website Services")}
              className={`px-8 py-3 rounded-full font-bold text-sm md:text-base transition-all duration-300 ${
                activeTab === "Website Services" 
                  ? "bg-[#f06600] text-white shadow-lg" 
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              Website Services
            </button>
            <button
              onClick={() => setActiveTab("Digital Marketing")}
              className={`px-8 py-3 rounded-full font-bold text-sm md:text-base transition-all duration-300 ${
                activeTab === "Digital Marketing" 
                  ? "bg-[#f06600] text-white shadow-lg" 
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              Digital Marketing
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <BeatLoader color="#f06600" size={15} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {Array.isArray(filteredPlans) && filteredPlans.map((plan, index) => (
              <div
                key={plan._id}
                className={`relative bg-white rounded-3xl p-8 flex flex-col transition-all duration-300 hover:-translate-y-2 ${
                  plan.isRecommended 
                    ? 'border-2 border-[#f06600] shadow-2xl z-10 transform md:scale-105' 
                    : 'border border-gray-100 shadow-lg hover:shadow-xl'
                }`}
                data-aos="fade-up"
                data-aos-delay={index * 150}
              >
                {plan.isRecommended && (
                  <div className="absolute top-0 inset-x-0 flex justify-center -mt-4">
                    <span className="bg-[#f06600] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md uppercase tracking-wider">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8 mt-2">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.isRecommended ? 'text-[#f06600]' : 'text-gray-800'}`}>
                    {plan.title}
                  </h3>
                  <div className="flex items-center justify-center mt-4">
                    <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                  </div>
                </div>

                <div className="flex-1">
                  <ul className="space-y-4 mb-8">
                    {Array.isArray(plan.features) && plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-600 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto pt-4">
                  <a 
                    href={`https://wa.me/923254543708?text=${encodeURIComponent(`Hi! I'm interested in your ${plan.title} plan (${plan.price}) from the ${plan.category} category. Can you provide more details?`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className={`block text-center w-full py-4 rounded-xl font-bold transition-all duration-300 shadow-md hover:shadow-lg ${
                      plan.isRecommended 
                        ? 'bg-[#f06600] text-white hover:bg-[#d05800]' 
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    Get Started Now
                  </a>
                </div>
              </div>
            ))}
            
            {Array.isArray(filteredPlans) && filteredPlans.length === 0 && (
              <div className="col-span-full text-center py-20 bg-white rounded-3xl shadow-sm border border-dashed border-gray-300">
                <p className="text-2xl text-gray-400 font-medium">Coming Soon</p>
                <p className="text-gray-500 mt-2">We are currently updating our packages for this category.</p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Pricing;
